import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, collection, getDocs, updateDoc, addDoc, deleteDoc, serverTimestamp, query, where, onSnapshot, limit, runTransaction } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Printer, FileText, ChevronDown, Copy, Trash2, Send, FileCheck } from 'lucide-react';
import PartsConfigTable from './components/PartsConfigTable.js';
import { PickupTimeline } from './components/HorizontalTimeline.js';
import useClickOutside from './hooks/useClickOutside.js';
import PCPreviewContent from './PCPreviewContent.js';

const getPickupActiveQty = (component = {}) => {
  const materialQty = Number(component.quantity || 0);
  const goodQty = Number(component.liftedGood || 0);
  const damageQty = Number(component.liftedDamage || 0);
  const missingQty = Number(component.finalMissing || 0);

  return Math.max(0, materialQty - goodQty - damageQty - missingQty);
};

const getPickupChallanSuffix = (type) => {
  return type === 'Rental Return' ? 'R' : 'S';
};

const buildPickupChallanNo = (inwardNo, type, pickupDate) => {
  if (!inwardNo) return '';

  const baseDate = pickupDate ? new Date(pickupDate) : new Date();
  const safeDate = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;
  const year = String(safeDate.getFullYear()).slice(-2);
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');

  return `PC/${year}/${month}/${inwardNo}/${getPickupChallanSuffix(type)}`;
};

const PCDetail = ({ db, user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pcData, setPcData] = useState(null);
  const [micData, setMicData] = useState(null);
  const [firms, setFirms] = useState([]);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const moreDropdownRef = useClickOutside(() => setShowMoreDropdown(false));
  const printRef = useRef();

  const [completionRemarks, setCompletionRemarks] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const isReadOnly = user?.role === 'accounts';
  const listPath = isReadOnly ? '/accounts/pickup-challan' : '/ops/pickup-challan';

  const getNextInwardNumber = async () => {
    const sequencesRef = doc(db, "counters", "inward_sequence");
    return await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      let currentCount = 2000;
      if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
        currentCount = sequenceDoc.data().count;
      }
      const nextCount = currentCount + 1;
      transaction.set(sequencesRef, { count: nextCount }, { merge: true });
      return currentCount.toString();
    });
  };

  useEffect(() => {
    let unsubscribePc = () => {};

    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        setFirms(firmSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const pcRef = doc(db, "inwards", id);
        unsubscribePc = onSnapshot(
          pcRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              const normalizedPcNo = data.pickupChallanNo || data.pcNo || data.inwardNo || '';
              setPcData({ id: docSnap.id, ...data, pcNo: normalizedPcNo });
              setCompletionRemarks(data.completionRemarks || '');
            } else {
              console.warn("PC Document no longer exists.");
            }
          },
          (error) => {
            console.error("Error fetching PC data:", error);
          }
        );

        // Fetch the associated MIC document (independent child record)
        const micQuery = query(collection(db, "inwards"), where("parentPcId", "==", id), limit(1));
        const unsubscribeMic = onSnapshot(micQuery, (snap) => {
            if (!snap.empty) {
                setMicData({ id: snap.docs[0].id, ...snap.docs[0].data() });
            } else {
                setMicData(null);
            }
        });

      } catch (error) {
        console.error("Error in fetchData:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => unsubscribePc();
  }, [db, id, navigate]);
  const handleStatusUpdate = async (newStatus, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    
    setIsSaving(true);
    try {
      const pcRef = doc(db, "inwards", id);
      const timestamp = serverTimestamp();
      const userName = user.displayName || user.email;

      if (newStatus === 'pc_completed') {
        const hasExistingCompletionFile = (pcData.completionFiles || []).length > 0;
        if (!selectedFiles.length && !hasExistingCompletionFile) {
          alert("Please upload the scanned pickup challan before confirming this Pickup Challan.");
          return;
        }

        let uploadedFiles = pcData.completionFiles || [];
        if (selectedFiles.length > 0) {
          uploadedFiles = await Promise.all(
            selectedFiles.map(async (file) => {
              const safeName = (file.name || 'file').replace(/[^\w.\-() ]+/g, '_');
              const path = `pc_completion/${id}/${Date.now()}_${safeName}`;
              const storageRef = ref(storage, path);
              await uploadBytes(storageRef, file);
              const url = await getDownloadURL(storageRef);
              return { name: file.name, type: file.type, size: file.size, url };
            })
          );
        }

        const finalizedComponents = (pcData.components || []).map((component) => ({
          ...component,
          totalLifted: (Number(component.liftedGood) || 0) + (Number(component.liftedDamage) || 0),
          totalActive: getPickupActiveQty(component)
        }));

        await updateDoc(pcRef, {
          status: 'pc_completed',
          completedAt: timestamp,
          completionRemarks: completionRemarks,
          completionFiles: uploadedFiles,
          components: finalizedComponents,
          updatedAt: timestamp,
          updatedBy: userName
        });

        const remainingComponents = finalizedComponents
          .map((component) => {
            const balance = Number(component.totalActive) || 0;
            if (balance <= 0) return null;

            return {
              ...component,
              quantity: balance,
              liftedGood: 0,
              liftedDamage: 0,
              totalLifted: 0,
              totalActive: balance,
              finalMissing: 0
            };
          })
          .filter(Boolean);

        if (remainingComponents.length > 0) {
          const nextInwardNo = await getNextInwardNumber();
          const nextPickupDate = new Date().toISOString().split('T')[0];
          const draftPayload = {
            rootInwardId: pcData.rootInwardId || pcData.id || id,
            orderId: pcData.orderId || '',
            type: pcData.type,
            referenceNo: pcData.referenceNo,
            firmId: pcData.firmId,
            inwardNo: nextInwardNo,
            pickupChallanNo: buildPickupChallanNo(nextInwardNo, pcData.type, nextPickupDate),
            pickupAgent: pcData.pickupAgent,
            coordinator: pcData.coordinator,
            customerID: pcData.customerID,
            customerName: pcData.customerName,
            billingCustomerName: pcData.billingCustomerName,
            billingAddress: pcData.billingAddress,
            siteCustomerName: pcData.siteCustomerName,
            siteProjectName: pcData.siteProjectName,
            siteAddress: pcData.siteAddress,
            gstin: pcData.gstin,
            salesperson: pcData.salesperson,
            orderBy: pcData.orderBy,
            sitePerson: pcData.sitePerson,
            remarks: pcData.remarks,
            completionRemarks: '',
            completionFiles: [],
            components: remainingComponents,
            handedOverBy: pcData.handedOverBy || { name: '', receivedBy: '', handoverDate: '', handoverTime: '' },
            receivedBy: pcData.receivedBy || '',
            receivedDate: pcData.receivedDate || '',
            receivedTime: pcData.receivedTime || '',
            verifiedBy: pcData.verifiedBy || '',
            status: 'pc_draft',
            dcDate: pcData.dcDate,
            pickupDate: nextPickupDate,
            intimationDate: new Date().toISOString().split('T')[0],
            createdAt: timestamp,
            createdBy: userName,
            updatedAt: timestamp,
            updatedBy: userName
          };
          await addDoc(collection(db, "inwards"), draftPayload);
        }
        
        alert(successMsg);
        setPcData(prev => ({ 
          ...prev, 
          status: 'pc_completed', 
          completionRemarks, 
          completionFiles: uploadedFiles,
          components: finalizedComponents
        }));
        return;
      }

      if (newStatus === 'mic_draft') {
        const pcNo = pcData.pickupChallanNo || pcData.inwardNo || pcData.pcNo || '';
        const micNo = pcData.inwardNo || '';
        const deliveryChallanNo = pcData.deliveryChallanNo || pcData.referenceNo || '';

        // 2. Update document with MIC fields
        await updateDoc(pcRef, {
          status: 'mic_draft',
          micStatus: 'draft',
          micDate: new Date().toISOString().split('T')[0],
          micNo: micNo,
          referenceNo: deliveryChallanNo,
          deliveryChallanNo: deliveryChallanNo,
          typeOfMIC: pcData.type === 'Rental Return' ? 'Rental Return' : (pcData.type === 'Sale Return' ? 'Sale Return' : ''),
          customerName: pcData.customerName || '',
          shippingAddress: pcData.siteAddress || '',
          preparedBy: userName,
          vehicleDateTime: new Date().toISOString().slice(0, 16),
          declarationDate: new Date().toISOString().split('T')[0],
          micComponents: (pcData.components || []).map(c => {
            const qty = Number(c.totalLifted || c.quantity || 0);
            const isRental = pcData.type === 'Rental Return';
            return {
              id: `${c.sku}-${Date.now()}`,
              sku: c.sku,
              name: c.name || c.description || '',
              newQty: isRental ? 0 : qty,
              rentalQty: isRental ? qty : 0,
              repairableQty: 0,
              nonRepairableQty: 0,
              weight: c.weight || 0,
              originalPcQuantity: qty
            };
          }),
          updatedAt: timestamp,
          updatedBy: userName
        });
        
        alert(successMsg);
        navigate(`/ops/mic/edit/${id}`);
        return;
      }

      // Normal status updates (e.g. pc_ready)
      const updatePayload = {
        status: newStatus,
        updatedAt: timestamp,
        updatedBy: userName
      };

      if (newStatus === 'pc_ready') {
        updatePayload.pcReadyAt = timestamp;
      }

      await updateDoc(pcRef, updatePayload);
      alert(successMsg);
      setPcData(prev => ({ ...prev, ...updatePayload, status: newStatus }));
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this Pickup Challan? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "inwards", id));
        alert("Pickup Challan deleted successfully.");
        navigate(listPath);
      } catch (error) {
        alert("Error deleting PC: " + error.message);
      }
    }
  };

  const handleClone = async () => {
    if (window.confirm("Clone this Pickup Challan as a new draft?")) {
      try {
        const { id: _, pcNo: __, createdAt: ___, updatedAt: ____, status: _____, ...cloneData } = pcData;
        const newPcNo = `CLONE-${pcData.pcNo}-${Date.now().toString().slice(-4)}`;
        
        const newDoc = {
          ...cloneData,
          pcNo: newPcNo,
          status: 'pc_draft',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: user.displayName || user.email
        };

        const docRef = await addDoc(collection(db, "inwards"), newDoc);
        alert("PC Cloned Successfully!");
        navigate(`/ops/pickup-challan/${docRef.id}`);
      } catch (error) {
        alert("Error cloning PC: " + error.message);
      }
    }
  };

  const handleExportPDF = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PC ${pcData?.pcNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
            @page { margin: 1cm; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;
  if (!pcData) return null;

  const selectedFirm = firms.find(f => f.id === pcData.firmId);
  const getStatusBadge = (status) => {
    const map = {
      pc_draft: { label: 'DRAFT', bg: '#f1f5f9', fg: '#334155', border: '#e2e8f0' },
      pc_ready: { label: 'READY', bg: '#ecfdf5', fg: '#065f46', border: '#bbf7d0' },
      pc_completed: { label: 'PICKUP COMPLETED', bg: '#f0fdf4', fg: '#166534', border: '#bbf7d0' },
      mic_draft: { label: 'MIC DRAFT', bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' },
      mic_completed: { label: 'MIC COMPLETED', bg: '#f0fdf4', fg: '#166534', border: '#bbf7d0' }
    };
    return map[status] || { label: (status || '—').toString().replaceAll('_', ' ').toUpperCase(), bg: '#f8fafc', fg: '#334155', border: '#e2e8f0' };
  };
  const statusBadge = getStatusBadge(pcData.status);
  const statusPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    height: '26px',
    padding: '0 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 800,
    letterSpacing: '0.04em',
    backgroundColor: statusBadge.bg,
    color: statusBadge.fg,
    border: `1px solid ${statusBadge.border}`,
    lineHeight: 1,
    whiteSpace: 'nowrap'
  };
  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(listPath)} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>PC Setup: {pcData?.pcNo}</span>
            <span style={statusPillStyle}>{statusBadge.label}</span>
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!isReadOnly && pcData.status === 'pc_draft' && (
            <>
              <button 
                onClick={() => navigate(`/ops/pickup-challan/edit/${id}`)} 
                style={editBtnStyle}
              >
                <Edit2 size={18} /> Edit PC
              </button>
              <button 
                onClick={() => handleStatusUpdate('pc_completed', "Confirm this Pickup Challan? Please double-check all quantities, remarks, and the scanned pickup challan before continuing. After confirmation, this record will be locked for editing and a new draft Pickup Challan will be created for any remaining active quantity.", "Pickup completed successfully.")} 
                disabled={isSaving} 
                style={confirmBtnStyle}
              >
                <FileCheck size={18} /> Confirm PC
              </button>
            </>
          )}

          {!isReadOnly && pcData.status === 'pc_completed' && (
            <button 
              onClick={() => handleStatusUpdate('mic_draft', "Proceed to initiate Material Inward Checklist (MIC)?", "MIC Draft initiated successfully.")} 
              disabled={isSaving}
              style={{ ...confirmBtnStyle, backgroundColor: '#2563eb' }}
            >
              <Send size={18} /> Proceed to MIC
            </button>
          )}

          {!isReadOnly && ['mic_draft', 'mic_ready', 'mic_completed'].includes(pcData.status) && (
            <button 
              onClick={() => navigate(`/ops/mic/preview/${id}`)}
              style={{ ...confirmBtnStyle, backgroundColor: '#2563eb' }}
            >
              <Send size={18} /> View MIC Checklist
            </button>
          )}
          
          <button onClick={handleExportPDF} style={secondaryBtnStyle}>
            <Printer size={18} /> Export PDF
          </button>

          {!isReadOnly && (
            <div style={{ position: 'relative' }} ref={moreDropdownRef}>
              <button 
                onClick={() => setShowMoreDropdown(!showMoreDropdown)} 
                style={{ ...secondaryBtnStyle, display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                More <ChevronDown size={18} />
              </button>
              {showMoreDropdown && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '5px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '150px', overflow: 'hidden' }}>
                  <button 
                    onClick={() => { setShowMoreDropdown(false); handleClone(); }}
                    style={dropdownItemStyle}
                    className="hover-highlight"
                  >
                    <Copy size={16} /> Clone PC
                  </button>
                  <button 
                    onClick={() => { setShowMoreDropdown(false); handleDelete(); }}
                    style={{ ...dropdownItemStyle, color: '#ef4444' }}
                    className="hover-highlight"
                  >
                    <Trash2 size={16} /> Delete PC
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <PickupTimeline
        pcData={pcData}
        micData={micData}
        title={`Pickup Activity: ${pcData?.pcNo || pcData?.inwardNo || pcData?.referenceNo || id}`}
      />

     

      <PCPreviewContent
        pcData={pcData}
        selectedFirm={selectedFirm}
        printRef={printRef}
      />

      {((pcData.completionRemarks && pcData.completionRemarks.trim()) || (pcData.completionFiles && pcData.completionFiles.length > 0) || ['pc_completed', 'mic_draft', 'mic_ready', 'mic_completed'].includes(pcData.status)) && (
        <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
            Completion Details
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Completion Remarks</div>
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', minHeight: '90px', whiteSpace: 'pre-wrap', color: '#0f172a', fontSize: '13px' }}>
                {pcData.completionRemarks || '—'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Attached Files</div>
              {(pcData.completionFiles && pcData.completionFiles.length > 0) ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {pcData.completionFiles.map((f, idx) => (
                    <a
                      key={`${f.url || f.name || 'file'}_${idx}`}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', textDecoration: 'none', color: '#0f172a', backgroundColor: '#ffffff' }}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name || 'Attachment'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{f.type || 'file'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', color: '#64748b', fontSize: '13px' }}>
                  No files attached.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const editBtnStyle = { padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: 'white' };
const confirmBtnStyle = { padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#059669', color: 'white' };
const secondaryBtnStyle = { padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' };

const dropdownItemStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 15px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: '14px',
  color: '#475569',
  transition: 'all 0.2s'
};

const statusBannerStyle = (status) => ({
  backgroundColor: '#f8fafc',
  padding: '12px 20px',
  borderRadius: '8px',
  marginBottom: '20px',
  border: '1px solid #e2e8f0'
});

export default PCDetail;
